import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import { translateSaveError } from '@/lib/mutation-result';
import { createHoliday, deleteHoliday, getHolidays, updateHoliday } from '../services/holiday.service';
import { GetHolidaysParams, HolidayInsertInput, HolidayUpdateInput } from '../types/holiday.types';

const invalidateHolidayQueries = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: ['hr-holidays'] });

export const useGetHolidays = (params: GetHolidaysParams) => {
  return useGlobalQuery({
    queryKey: ['hr-holidays', params],
    queryFn: () => getHolidays(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateHoliday = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (input: HolidayInsertInput) => translateSaveError(t, createHoliday(input)),
    onSuccess: () => {
      invalidateHolidayQueries(queryClient);
      toast({ title: t('HOLIDAY_CREATED') });
    },
  });
};

export const useUpdateHoliday = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: { itemId: string; input: HolidayUpdateInput }) =>
      translateSaveError(t, updateHoliday(params)),
    onSuccess: () => {
      invalidateHolidayQueries(queryClient);
      toast({ title: t('HOLIDAY_UPDATED') });
    },
  });
};

export const useDeleteHoliday = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (itemId: string) => translateSaveError(t, deleteHoliday(itemId)),
    onSuccess: () => {
      invalidateHolidayQueries(queryClient);
      toast({ title: t('HOLIDAY_DELETED') });
    },
  });
};
